'use client';

import React from 'react';
import type { Platform } from '@/lib/rateCalculator';
import { useLanguage } from '@/context/LanguageContext';

interface PlatformSelectorProps {
  selected: Platform;
  onChange: (platform: Platform) => void;
}

// SVG icons for platforms
function InstagramIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig-grad)" />
      <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
      <defs>
        <linearGradient id="ig-grad" x1="0" y1="24" x2="24" y2="0">
          <stop stopColor="#FCAF45" />
          <stop offset="0.5" stopColor="#E1306C" />
          <stop offset="1" stopColor="#833AB4" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function TikTokIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="6" fill="#010101" />
      <path d="M16.5 5.5c.4 1.5 1.5 2.5 3 2.8v2.5c-1.2-.1-2.3-.5-3-1.2v5.4c0 2.8-2.2 5-5 5s-5-2.2-5-5 2.2-5 5-5c.3 0 .6 0 .9.1v2.6c-.3-.1-.6-.1-.9-.1-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5V5.5h2.5Z" fill="white" />
      <path d="M16.5 5.5c.4 1.5 1.5 2.5 3 2.8v2.5c-1.2-.1-2.3-.5-3-1.2" fill="#69C9D0" />
    </svg>
  );
}

function YouTubeIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="6" fill="#FF0000" />
      <path d="M20 8.5s-.2-1.3-.8-1.9c-.7-.8-1.6-.8-2-.8C15.2 5.6 12 5.6 12 5.6s-3.2 0-5.2.2c-.4 0-1.3 0-2 .8-.6.6-.8 1.9-.8 1.9S3.8 9.9 3.8 11.4v1.4c0 1.4.2 2.9.2 2.9s.2 1.3.8 1.9c.7.8 1.7.7 2.2.8C8.4 18.5 12 18.5 12 18.5s3.2 0 5.2-.2c.4 0 1.3 0 2-.8.6-.6.8-1.9.8-1.9s.2-1.4.2-2.9v-1.4C20.2 9.9 20 8.5 20 8.5Z" fill="white" />
      <path d="M10 14.5v-5l5 2.5-5 2.5Z" fill="#FF0000" />
    </svg>
  );
}

const platformIcons: Record<Platform, React.ReactNode> = {
  instagram: <InstagramIcon size={28} />,
  tiktok: <TikTokIcon size={28} />,
  youtube: <YouTubeIcon size={28} />,
};

export default function PlatformSelector({ selected, onChange }: PlatformSelectorProps) {
  const { t } = useLanguage();

  const platforms: { id: Platform; label: string; description: string }[] = [
    { id: 'instagram', label: t.platforms.instagram, description: t.platforms.instagramDesc },
    { id: 'tiktok', label: t.platforms.tiktok, description: t.platforms.tiktokDesc },
    { id: 'youtube', label: t.platforms.youtube, description: t.platforms.youtubeDesc },
  ];

  return (
    <div>
      <label className="block text-sm font-semibold text-foreground mb-3 tracking-wide">
        {t.form.platform}
      </label>
      <div className="grid grid-cols-3 gap-3">
        {platforms.map((platform) => (
          <button
            key={`platform-${platform.id}`}
            type="button"
            onClick={() => onChange(platform.id)}
            className={`platform-card flex flex-col items-center gap-2 p-3 text-center ${
              selected === platform.id ? 'selected' : ''
            }`}
          >
            <div className="w-10 h-10 flex items-center justify-center">
              {platformIcons[platform.id]}
            </div>
            <div>
              <div className="text-xs font-bold text-foreground">{platform.label}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                {platform.description}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}